// src/components/OrganizationSetup.js

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Navigate } from 'react-router-dom'; // Added Navigate
import { showToast } from '../utils/toast';
import { fetchUserProfile, updateUserProfile } from '../redux/slices/authSlice'; // Ensure updateUserProfile is used correctly or removed if backend handles flag update
import api from '../utils/api';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';

const OrganizationSetup = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useSelector((state) => state.auth);
  const [organization, setOrganization] = useState(null);
  const [step, setStep] = useState(1);
  const [loadingOrg, setLoadingOrg] = useState(true); // Renamed loading state
  const [inviteEmails, setInviteEmails] = useState('');
  const [orgFormData, setOrgFormData] = useState({
    name: '',
    domain: '',
    description: ''
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [initialCheckDone, setInitialCheckDone] = useState(false); // Flag to prevent infinite loops

  // --- Fetch User Data ---
  useEffect(() => {
    const loadInitialData = async () => {
      setLoadingOrg(true);
      if (isAuthenticated && !user) {
        try {
          // Fetch user profile if not already loaded
          await dispatch(fetchUserProfile()).unwrap();
        } catch (err) {
          console.error("Error fetching user profile:", err);
          showToast.error("Failed to load user data. Please refresh.");
          setLoadingOrg(false);
          return; // Stop if user fetch fails
        }
      }
      setInitialCheckDone(false); // Reset check flag when user context changes or on initial load
      setLoadingOrg(false);
    };
    loadInitialData();
  }, [dispatch, isAuthenticated]); // Depend only on authentication status


  // --- Effect to check organization status AFTER user is loaded ---
  useEffect(() => {
    const checkOrganization = async () => {
      // Only run if user is loaded, authenticated, auth is not loading, and initial check hasn't run for this user state
      if (user && isAuthenticated && !authLoading && !initialCheckDone) {
        setLoadingOrg(true);
        setInitialCheckDone(true); // Mark check as started for this user state

         // Set initial form data from pendingOrganization if it exists
         if (user.pendingOrganization) {
            setOrgFormData({
                name: user.pendingOrganization.name || '',
                domain: user.pendingOrganization.domain || '',
                description: user.pendingOrganization.description || ''
            });
        }

        // If user needs setup, primarily show the form
        if (user.needsOrganizationSetup) {
          // Optionally: Make ONE attempt to fetch org in case setup was interrupted
          try {
            const response = await api.get('/organizations');
            if (response.data) {
              setOrganization(response.data);
               // If an org IS found, user might not need setup anymore. Re-fetch profile?
               // Or directly navigate away / update state. For now, we assume setup is still needed.
               console.warn("User needs setup, but an organization was found. Proceeding with setup form based on user flag.");
               setStep(2); // Maybe jump to verification?
            }
          } catch (error) {
             if (error.response && error.response.status === 404) {
               console.log("No existing organization found for user needing setup. Displaying creation form.");
               // Stay on Step 1 (or ensure it's step 1)
               setStep(1);
             } else if (error.response && error.response.status !== 429) { // Ignore 429 during this check
               console.error("Error checking for existing organization:", error);
               // Optionally show a non-blocking error
             }
          } finally {
             setLoadingOrg(false);
          }
        } else if (user.organization) {
           // If setup NOT needed and org ID exists, fetch the org details
           try {
              console.log(`Workspaceing existing organization for user ${user._id} with org ID ${user.organization}`); // Debug log
              const response = await api.get('/organizations'); // Endpoint fetches based on logged-in user
              setOrganization(response.data);
              // User has an org and doesn't need setup, redirect to dashboard
              console.log("User already has an organization, redirecting to dashboard.");
              navigate('/admin-dashboard', { replace: true });
              // setStep(4); // Or move to a "completed" state if staying here (though redirect is better)
           } catch (error) {
              console.error("Error fetching existing organization details:", error);
               if (error.response && error.response.status !== 429){
                 showToast.error("Could not load your organization details.");
               }
               // Decide how to handle this - maybe redirect to a general dashboard?
               navigate('/marketplace', { replace: true }); // Fallback redirect
           } finally {
             setLoadingOrg(false);
           }
        } else {
          // User doesn't need setup and has no organization - redirect to marketplace
          console.log("User doesn't need setup and has no organization ID.");
          navigate('/marketplace', { replace: true });
          setLoadingOrg(false);
        }
      } else if (!isAuthenticated && !authLoading) {
        // If user is not authenticated and loading is finished, clear state
        setLoadingOrg(false);
        setOrganization(null);
        setStep(1);
      }
    };

    checkOrganization();
    // Depend on user object changes (including null -> loaded) to re-trigger the check
  }, [user, isAuthenticated, authLoading, initialCheckDone, navigate, dispatch]);

  // Handle organization form input changes
  const handleOrgFormChange = (e) => {
    const { name, value } = e.target;
    setOrgFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle organization form submission
  const handleCreateOrganization = async (e) => {
    e.preventDefault();

    if (!orgFormData.name || !orgFormData.domain) {
      showToast.error('Organization name and domain are required');
      return;
    }

    // Crucially, check email verification *before* attempting creation
    if (!user.isEmailVerified) {
        showToast.warning('Please verify your email before creating an organization.');
        // Optionally, navigate to a page or show a component to resend verification
        navigate('/resend-verification');
        return;
    }

    setFormSubmitting(true);

    try {
      // Use the user's email from the Redux state as the admin email
      const adminEmail = user.email;
      if (!adminEmail) {
          throw new Error("Admin email is missing."); // Should not happen if user is loaded
      }

      const createOrgResponse = await api.post('/organizations', {
          name: orgFormData.name,
          domain: orgFormData.domain,
          description: orgFormData.description,
          email: adminEmail // Pass the verified user's email
      });

      setOrganization(createOrgResponse.data.organization);
      showToast.success('Organization created successfully! Check email for domain verification.');

      // Re-fetch user profile AFTER successful org creation
      // This ensures needsOrganizationSetup is updated to false
      await dispatch(fetchUserProfile());

      setStep(2); // Move to domain verification step

    } catch (error) {
      console.error('Error creating organization:', error);
      const errorMsg = error.response?.data?.message || 'Failed to create organization.';

      if (error.response?.status === 403) {
          showToast.error('Email verification required. Please verify your email first.');
          // Optionally navigate to resend verification page
           navigate('/resend-verification');
      } else if (error.response?.status === 400) {
           // Handle specific backend errors like domain already exists
          showToast.error(errorMsg);
      } else {
          showToast.error(errorMsg);
      }
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();

    const emails = inviteEmails
      .split(/[\n,;]/)
      .map(email => email.trim())
      .filter(email => email.length > 0);

    if (emails.length === 0) {
      showToast.error('Please enter at least one email address');
      return;
    }

    try {
      await api.post('/organizations/invite', { emails });
      showToast.success('Invitations sent successfully!');
      setStep(4);
    } catch (error) {
      console.error('Error sending invitations:', error);
      showToast.error('Failed to send invitations');
    }
  };

  const handleFinish = () => {
    navigate('/admin-dashboard');
  };

  // --- Render Logic ---
   if (authLoading || loadingOrg) {
     return (
       <div className="flex justify-center items-center min-h-screen">
         <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
       </div>
     );
   }

   if (!isAuthenticated) {
     // Redirect to login or show login message if needed
     return <Navigate to="/login" replace />;
   }

    // Add check for email verification before showing the form
    // This check is now primarily within the handleCreateOrganization submit handler,
    // but we can also show a message if they land here unverified.
    if (user && !user.isEmailVerified && user.needsOrganizationSetup && step === 1) {
        return (
            <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded-lg shadow-md">
            <h2 className="text-xl font-bold text-center mb-4">Email Verification Required</h2>
            <p className="text-center text-gray-600 mb-4">
                Please verify your email before continuing with organization setup.
                Check your inbox for the verification link.
            </p>
            <div className="mt-6 flex justify-center">
                <button
                onClick={() => navigate('/resend-verification')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                Resend Verification Email
                </button>
            </div>
            </div>
        );
    }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-center">
              {step === 1 && 'Welcome to PerkExchange for Business'}
              {step === 2 && 'Verify Your Domain'}
              {step === 3 && 'Invite Your Team'}
              {step === 4 && 'Setup Complete!'}
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-6">
            {step === 1 && (
              <div>
                <h3 className="text-lg font-medium mb-4 text-center">
                  Let's set up your organization
                </h3>
                <p className="mb-6 text-center">
                  We'll guide you through a few quick steps to get your organization ready.
                </p>

                <form onSubmit={handleCreateOrganization} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Organization Name*
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={orgFormData.name}
                      onChange={handleOrgFormChange}
                      required
                      placeholder="Acme Corp"
                      className="block w-full rounded-md border border-gray-300 p-2 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="domain" className="block text-sm font-medium text-gray-700 mb-1">
                      Organization Domain*
                    </label>
                    <input
                      type="text"
                      id="domain"
                      name="domain"
                      value={orgFormData.domain}
                      onChange={handleOrgFormChange}
                      required
                      placeholder="acmecorp.com"
                      className="block w-full rounded-md border border-gray-300 p-2 focus:border-blue-500 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      This should be your company's email domain (e.g., acmecorp.com)
                    </p>
                  </div>

                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                      Description (Optional)
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      rows={3}
                      value={orgFormData.description}
                      onChange={handleOrgFormChange}
                      placeholder="A few words about your organization"
                      className="block w-full rounded-md border border-gray-300 p-2 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>

                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={formSubmitting || !user?.isEmailVerified} // Disable if submitting OR email not verified
                      className={`w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 ${(!user?.isEmailVerified || formSubmitting) ? 'opacity-60 cursor-not-allowed' : ''}`}
                      title={!user?.isEmailVerified ? "Please verify your email first" : ""}
                    >
                      {formSubmitting ? 'Creating...' : 'Create Organization'}
                    </button>
                  </div>
                </form>

                {/* Add Skip Setup button here */}
                <div className="mt-6 flex justify-center space-x-4">
                  <button
                    type="button"
                    onClick={() => navigate('/admin-dashboard')}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                  >
                    Skip Setup (Go to Admin Dashboard)
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h3 className="text-lg font-medium mb-4">
                  Domain Verification Status: {organization?.verificationStatus === 'verified' ?
                    'Verified ✓' : 'Pending Verification'}
                </h3>

                {organization?.verificationStatus !== 'verified' ? (
                  <div className="bg-yellow-50 p-4 rounded-md">
                    <p className="text-yellow-700 mb-2">
                      We've sent a verification email to your address ({user?.email}). Please check your inbox and click the verification link.
                    </p>
                    <p className="text-sm text-yellow-600">
                      Once verified, employees with your domain ({organization?.domain}) can automatically join your organization.
                    </p>
                    {/* Add a resend verification button if needed */}
                  </div>
                ) : (
                  <div className="bg-green-50 p-4 rounded-md">
                    <p className="text-green-700">
                      Your domain {organization.domain} has been successfully verified!
                    </p>
                  </div>
                )}

                <div className="mt-6 flex justify-between">
                  <button
                    onClick={() => setStep(1)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Continue
                  </button>
                </div>

                {/* Add Skip to Admin Dashboard button here too */}
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={() => navigate('/admin-dashboard')}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                  >
                    Skip to Admin Dashboard
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <h3 className="text-lg font-medium mb-4">
                  Invite Team Members
                </h3>
                <p className="mb-4 text-gray-600">
                  Invite your colleagues to join your organization on PerkExchange.
                </p>

                <form onSubmit={handleInviteSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="inviteEmails" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Addresses
                    </label>
                    <textarea
                      id="inviteEmails"
                      rows={5}
                      placeholder="Enter email addresses, separated by commas or new lines"
                      value={inviteEmails}
                      onChange={(e) => setInviteEmails(e.target.value)}
                      className="block w-full rounded-md border border-gray-300 p-2 focus:border-blue-500 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      You can always invite more team members later.
                    </p>
                  </div>

                  <div className="flex justify-between">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                    >
                      Back
                    </button>
                    <div className="space-x-2">
                      <button
                        type="button"
                        onClick={() => setStep(4)}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                      >
                        Skip
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        Send Invitations
                      </button>
                    </div>
                  </div>

                  {/* Add Skip to Admin Dashboard button here too */}
                  <div className="mt-4 flex justify-center">
                    <button
                      type="button"
                      onClick={() => navigate('/admin-dashboard')}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                    >
                      Skip to Admin Dashboard
                    </button>
                  </div>
                </form>
              </div>
            )}

            {step === 4 && (
              <div className="text-center">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium mb-2">Setup Complete!</h3>
                <p className="mb-6 text-gray-600">
                  Your organization is now set up and ready to use PerkExchange for Business.
                </p>
                <button
                  onClick={handleFinish}
                  className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Go to Admin Dashboard
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OrganizationSetup;